/**
 * @kmi/contracts — per-service-type package configuration (Task 151-A).
 *
 * Describes the shape of the freeform `config` jsonb persisted per
 * `agency_package_services` line (migration
 * `20260601120000_agency_package_service_config.sql`). The package
 * builder (Task 151-B) constructs/reads these per the included
 * service's type.
 *
 * Types + enum arrays ONLY — no runtime logic, per the package charter
 * (this file owns nothing at runtime except enum arrays + string
 * unions). The per-type normalization/coercion lives in the 151-B
 * frontend, where the config objects are built and edited.
 */

export const PACKAGE_SERVICE_TYPES = [
  'social',
  'ads',
  'influencer',
  'production',
  'content',
  'brand',
] as const;
export type PackageServiceType = (typeof PACKAGE_SERVICE_TYPES)[number];

export const SOCIAL_PLATFORM_KEYS = [
  'instagram',
  'tiktok',
  'snapchat',
  'twitter',
  'facebook',
  'youtube',
  'linkedin',
] as const;
export type SocialPlatformKey = (typeof SOCIAL_PLATFORM_KEYS)[number];

export const AD_PLATFORM_KEYS = ['meta', 'google', 'tiktok', 'snapchat', 'x', 'linkedin'] as const;
export type AdPlatformKey = (typeof AD_PLATFORM_KEYS)[number];

export const ADS_PRICING_MODES = ['fixed', 'percentage', 'hybrid'] as const;
export type AdsPricingMode = (typeof ADS_PRICING_MODES)[number];

export const PRODUCTION_TYPE_KEYS = [
  'reels',
  'tiktok',
  'pro_photo',
  'menu_photo',
  'opening_photo',
] as const;
export type ProductionTypeKey = (typeof PRODUCTION_TYPE_KEYS)[number];

export const SOCIAL_CONTENT_STRATEGIES = ['unified', 'custom'] as const;
export type SocialContentStrategy = (typeof SOCIAL_CONTENT_STRATEGIES)[number];

export interface SocialContentQuota {
  readonly designs: number;
  readonly videos: number;
  readonly stories: number;
}

export interface SocialServiceConfig {
  readonly platforms: ReadonlyArray<SocialPlatformKey>;
  readonly monthly: SocialContentQuota;
  readonly weekly: SocialContentQuota;
  readonly strategy: SocialContentStrategy;
  readonly reply: boolean;
}

export interface AdsServiceConfig {
  readonly pricing: AdsPricingMode;
  readonly amount: number | null;
  readonly percentage: number | null;
  readonly platforms: ReadonlyArray<AdPlatformKey>;
  readonly details: string;
}

export interface InfluencerServiceConfig {
  readonly count: number;
  readonly category: string;
  readonly details: string;
}

export interface ProductionServiceConfig {
  readonly photos: number;
  readonly videos: number;
  readonly types: ReadonlyArray<ProductionTypeKey>;
}

export interface ContentServiceConfig {
  readonly monthly_count: number;
  readonly caption: boolean;
  readonly strategy: boolean;
  readonly seo: boolean;
}

export interface BrandServiceConfig {
  readonly logo: boolean;
  readonly identity: boolean;
  readonly guidelines: boolean;
  readonly extras: ReadonlyArray<string>;
  readonly notes: string;
}

/** Maps each service type to its per-line config shape. */
export interface PackageServiceConfigByType {
  readonly social: SocialServiceConfig;
  readonly ads: AdsServiceConfig;
  readonly influencer: InfluencerServiceConfig;
  readonly production: ProductionServiceConfig;
  readonly content: ContentServiceConfig;
  readonly brand: BrandServiceConfig;
}
